import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { I18nT } from '@runtime/core/i18n/i18n'

import { Button } from '@runtime/components/ui/Button'
import { FormField } from '@runtime/components/ui/Form'
import { Text } from '@runtime/components/ui/Text'
import FieldGroup from '@runtime/features/profile/auth/fields/FieldGroup'
import PasswordField from '@runtime/features/profile/auth/fields/PasswordField'
import styles from '@runtime/features/profile/auth/tabs/RegisterTab.module.scss'

type RegisterTabProps = {
  t: I18nT
  tNode: (
    key: string,
    tokens?: Record<string, ReactNode | (() => ReactNode)>,
  ) => ReactNode
  registerPseudo: string
  registerEmail: string
  registerPassword: string
  showRegisterPassword: boolean
  authError: string
  authBusy: boolean
  onRegisterPseudoChange: (value: string) => void
  onRegisterEmailChange: (value: string) => void
  onRegisterPasswordChange: (value: string) => void
  onToggleRegisterPassword: () => void
  onRegister: () => void
  onCloseModal: () => void
}

export default function RegisterTab({
  t,
  tNode,
  registerPseudo,
  registerEmail,
  registerPassword,
  showRegisterPassword,
  authError,
  authBusy,
  onRegisterPseudoChange,
  onRegisterEmailChange,
  onRegisterPasswordChange,
  onToggleRegisterPassword,
  onRegister,
  onCloseModal,
}: RegisterTabProps) {
  return (
    <div className={styles.loginForm} data-testid="login-form">
      <div className={styles.loginSectionTitle}>
        {t('profile.auth.registerTitle')}
      </div>
      {authError && (
        <Text variant="legend" className={styles.noticeError}>
          {authError}
        </Text>
      )}
      <FieldGroup label={t('online.profile.nick')}>
        <FormField
          round
          value={registerPseudo}
          onChange={(event) => onRegisterPseudoChange(event.target.value)}
          placeholder={t('profile.identity.pseudoPlaceholder')}
          disabled={authBusy}
        />
      </FieldGroup>
      <FieldGroup label={t('profile.identity.emailLabel')}>
        <FormField
          round
          value={registerEmail}
          onChange={(event) => onRegisterEmailChange(event.target.value)}
          placeholder={t('profile.identity.emailPlaceholder')}
          disabled={authBusy}
        />
      </FieldGroup>
      <FieldGroup label={t('profile.identity.passwordLabel')}>
        <PasswordField
          value={registerPassword}
          visible={showRegisterPassword}
          placeholder={t('profile.identity.passwordPlaceholder')}
          disabled={authBusy}
          showLabel={t('profile.auth.showPassword')}
          hideLabel={t('profile.auth.hidePassword')}
          onChange={onRegisterPasswordChange}
          onToggleVisibility={onToggleRegisterPassword}
          onKeyDown={(event) => {
            if (event.key === 'Enter') onRegister()
          }}
        />
      </FieldGroup>
      <div className={styles.loginFormFooter} data-testid="login-form-footer">
        <Button
          type="button"
          data-testid="register-submit"
          onClick={onRegister}
          disabled={authBusy}
        >
          {authBusy
            ? t('profile.auth.loading')
            : t('profile.auth.registerAction')}
        </Button>
      </div>
      <div className={styles.loginLegal}>
        {tNode('profile.auth.privacyNotice', {
          link: () => (
            <Link
              to="/privacy"
              onClick={onCloseModal}
              data-testid="login-legal-link"
            >
              {t('profile.auth.privacyLink')}
            </Link>
          ),
        })}
      </div>
    </div>
  )
}
